/**
 * Dünner Fetch-Wrapper um die JSON-API.
 *
 * Alle Methoden werfen bei Fehlern einen `Error` mit der Servermeldung
 * (bzw. einer verständlichen deutschen Ersatzmeldung).
 */

export type QueryValue = string | number | boolean | undefined | null;

export interface QueryParams {
  readonly [key: string]: QueryValue;
}

/** Ist der Wert ein einfaches Objekt (Record)? */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Macht aus einem unbekannten Fehlerwert eine anzeigbare Meldung. */
export function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  if (typeof error === 'string' && error.trim() !== '') return error;
  return 'Unbekannter Fehler.';
}

function buildUrl(path: string, params?: QueryParams): string {
  if (!params) return path;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query === '' ? path : `${path}?${query}`;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text.trim() === '') return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

function serverError(status: number, data: unknown): Error {
  if (isRecord(data) && typeof data['error'] === 'string' && data['error'].trim() !== '') {
    return new Error(data['error']);
  }
  if (status === 401) return new Error('Nicht angemeldet.');
  if (status === 403) return new Error('Keine Berechtigung für diese Aktion.');
  if (status === 404) return new Error('Nicht gefunden.');
  if (status === 503) return new Error('Der Dienst ist derzeit nicht verfügbar.');
  return new Error(`Serverfehler (HTTP ${status}).`);
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  path: string,
  options: { params?: QueryParams; body?: unknown } = {},
): Promise<T> {
  const hasBody = options.body !== undefined;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.params), {
      method,
      credentials: 'same-origin',
      headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
      body: hasBody ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new Error('Der Server ist nicht erreichbar. Bitte Verbindung prüfen.');
  }

  const data = await parseBody(response);
  if (!response.ok) throw serverError(response.status, data);
  return data as T;
}

export const api = {
  get<T>(path: string, params?: QueryParams): Promise<T> {
    return request<T>('GET', path, { params });
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>('POST', path, { body: body ?? {} });
  },
  patch<T>(path: string, body: unknown): Promise<T> {
    return request<T>('PATCH', path, { body });
  },
  del<T>(path: string): Promise<T> {
    return request<T>('DELETE', path);
  },
};
